import { useState } from 'react';
import { Link } from 'react-router-dom';
import { directoryTypeDef, type ProfessionalListItem } from '../../services/professionalsService';
import { trackContact } from '../../services/analytics';
import { formatAmount } from '../../utils/numberFormat';

const CONTACT_BUTTON =
  'relative z-20 grid h-10 w-10 shrink-0 place-items-center rounded-full border bg-[var(--pv-color-surface)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30';

/**
 * Directory card for one professional, in the trusted-brokers BrokerCard pattern
 * (owner, 2026-10-07): the whole card opens the unified /professionals/:id
 * profile; the name carries the gold "verified" mark; the role and individual /
 * company read as one muted line instead of two coloured tags; call and WhatsApp
 * are small outline buttons (WhatsApp keeps its green as an outline, never a fill).
 */
export default function ProfessionalCard({ pro, isRTL }: { pro: ProfessionalListItem; isRTL: boolean }) {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const isCompany = pro.ownerType === 'company';
  const def = directoryTypeDef(pro.activityType);
  const roleLabel = def ? (isRTL ? def.singularAr : def.singularEn) : pro.activityType;
  const name = pro.displayName || (isRTL ? 'مختصّ عقاري' : 'Professional');
  const initials = (name || '?').charAt(0).toUpperCase();
  const callNumber = pro.businessPhone || undefined;
  const waNumber = (pro.whatsapp || pro.businessPhone || '').replace(/[^0-9]/g, '');
  const contactEntity = isCompany ? 'company' : 'agent';
  const showAvatar = Boolean(pro.avatarUrl) && !avatarFailed;
  const verified = isRTL ? 'موثّق' : 'Verified';

  return (
    <article className="group relative flex h-full flex-col rounded-[16px] border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface)] p-5 transition-shadow duration-200 hover:shadow-brand-md">
      {/* The whole card opens the profile; the contact buttons sit above this link. */}
      <Link
        to={`/professionals/${pro.id}`}
        aria-label={name}
        className="absolute inset-0 z-10 rounded-[16px] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--pv-color-primary)]"
      />

      <div className="flex items-center gap-3">
        {showAvatar ? (
          <img
            src={pro.avatarUrl ?? undefined}
            alt=""
            className={
              isCompany
                ? 'h-14 w-14 shrink-0 rounded-xl border border-[var(--pv-border-soft)] bg-white object-contain p-1'
                : 'h-14 w-14 shrink-0 rounded-full object-cover'
            }
            onError={() => setAvatarFailed(true)}
          />
        ) : (
          <span
            className={`grid h-14 w-14 shrink-0 place-items-center bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)] ${
              isCompany ? 'rounded-xl' : 'rounded-full'
            }`}
            aria-hidden="true"
          >
            {isCompany ? (
              <i className="ri-building-2-line text-[1.375rem] leading-none" />
            ) : (
              <span className="text-[1.25rem] font-bold">{initials}</span>
            )}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <h3 className="flex items-center gap-1.5 text-[1rem] font-semibold text-[var(--pv-color-text)]">
            <span className="truncate">{name}</span>
            <i
              className="ri-verified-badge-fill shrink-0 text-[1.125rem] leading-none text-[#B58A0A]"
              role="img"
              aria-label={verified}
              title={verified}
            />
          </h3>
          <p className="mt-1 truncate text-[0.875rem] text-[var(--pv-color-text-muted)]">
            {roleLabel} · {isCompany ? (isRTL ? 'شركة' : 'Company') : (isRTL ? 'فرد' : 'Individual')}
          </p>
        </div>
      </div>

      {(pro.city || pro.avgRating > 0) && (
        <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[0.875rem] text-[var(--pv-color-text-muted)]">
          {pro.city && (
            <span className="flex min-w-0 items-center gap-1">
              <i className="ri-map-pin-line text-base leading-none" aria-hidden="true" />
              <span className="truncate">{pro.city}</span>
            </span>
          )}
          {pro.avgRating > 0 && (
            <span className="flex items-center gap-1 tabular-nums">
              <i className="ri-star-fill text-base leading-none text-[#B58A0A]" aria-hidden="true" />
              <span className="font-semibold text-[var(--pv-color-text)]">{pro.avgRating.toFixed(1)}</span>
              {pro.totalReviews > 0 && <span>({formatAmount(pro.totalReviews)})</span>}
            </span>
          )}
        </p>
      )}

      {pro.bio && <p className="mt-2 line-clamp-2 text-[0.875rem] leading-relaxed text-[var(--pv-color-text-muted)]">{pro.bio}</p>}

      <div className="mt-auto flex items-center justify-between gap-3 pt-4">
        <span className="inline-flex items-center gap-1 text-[0.875rem] font-semibold text-[var(--pv-color-primary)]">
          {isRTL ? 'عرض الملف' : 'View profile'}
          <i className={`${isRTL ? 'ri-arrow-left-line' : 'ri-arrow-right-line'} text-base leading-none`} aria-hidden="true" />
        </span>
        <div className="flex items-center gap-2">
          {callNumber && (
            <a
              href={`tel:${callNumber}`}
              onClick={() => trackContact(contactEntity, pro.id, 'call')}
              aria-label={isRTL ? `اتصال بـ ${name}` : `Call ${name}`}
              className={`${CONTACT_BUTTON} border-[var(--pv-border-strong)] text-[var(--pv-color-primary)] hover:border-[var(--pv-color-primary)]`}
            >
              <i className="ri-phone-line text-[1.125rem] leading-none" aria-hidden="true" />
            </a>
          )}
          {waNumber && (
            <a
              href={`https://wa.me/${waNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContact(contactEntity, pro.id, 'whatsapp')}
              aria-label={isRTL ? `واتساب ${name}` : `WhatsApp ${name}`}
              className={`${CONTACT_BUTTON} border-[#25D366]/60 text-[#1DA851] hover:border-[#25D366]`}
            >
              <i className="ri-whatsapp-line text-[1.125rem] leading-none" aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
